import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Edit, Trash2 } from "lucide-react";
import { usePromptStore } from "../../store/promptStore";

export default function TagManager() {
  const navigate = useNavigate();
  const { tags, loading, error, fetchTags, createTag, updateTag, deleteTag } =
    usePromptStore();

  const [formData, setFormData] = useState({
    name: "",
    slug: "",
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(
    null,
  );
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }

    if (!formData.slug.trim()) {
      errors.slug = "Slug is required";
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      errors.slug =
        "Slug can only contain lowercase letters, numbers, and hyphens";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (editingId) {
        await updateTag(editingId, formData);
      } else {
        await createTag(formData);
      }

      setFormData({ name: "", slug: "" });
      setEditingId(null);
    } catch (error) {
      console.error("Error saving tag:", error);
    }
  };

  const handleEdit = (tag: any) => {
    setFormData({
      name: tag.name,
      slug: tag.slug,
    });
    setEditingId(tag.id);
  };

  const handleDelete = async (id: string) => {
    await deleteTag(id);
    setShowDeleteConfirm(null);
  };

  const handleCancel = () => {
    setFormData({ name: "", slug: "" });
    setEditingId(null);
    setFormErrors({});
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setFormData((prev) => ({
      ...prev,
      name,
      // Auto-generate slug if not editing an existing tag
      slug: editingId
        ? prev.slug
        : name
            .toLowerCase()
            .replace(/\s+/g, "-")
            .replace(/[^a-z0-9-]/g, ""),
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center">
            <button
              onClick={() => navigate("/prompt-admin")}
              className="mr-4 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Tags</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Form */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                {editingId ? "Edit Tag" : "Add New Tag"}
              </h2>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      value={formData.name}
                      onChange={handleNameChange}
                      className={`mt-1 block w-full rounded-md shadow-sm ${
                        formErrors.name ? "border-red-300" : "border-gray-300"
                      } focus:border-[#F98B3D] focus:ring-[#F98B3D]`}
                    />
                    {formErrors.name && (
                      <p className="mt-1 text-sm text-red-600">
                        {formErrors.name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="slug"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Slug *
                    </label>
                    <input
                      type="text"
                      name="slug"
                      id="slug"
                      value={formData.slug}
                      onChange={(e) =>
                        setFormData({ ...formData, slug: e.target.value })
                      }
                      className={`mt-1 block w-full rounded-md shadow-sm ${
                        formErrors.slug ? "border-red-300" : "border-gray-300"
                      } focus:border-[#F98B3D] focus:ring-[#F98B3D]`}
                    />
                    {formErrors.slug && (
                      <p className="mt-1 text-sm text-red-600">
                        {formErrors.slug}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Used in URLs. Only lowercase letters, numbers, and
                      hyphens.
                    </p>
                  </div>

                  <div className="flex justify-end space-x-3">
                    {editingId && (
                      <button
                        type="button"
                        onClick={handleCancel}
                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      type="submit"
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
                    >
                      <Plus className="h-5 w-5 mr-2" />
                      {editingId ? "Update Tag" : "Add Tag"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Tags List */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Tags</h2>

              {loading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F98B3D]"></div>
                </div>
              ) : error ? (
                <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
                  <p className="text-red-700">{error}</p>
                </div>
              ) : tags.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No tags found</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <div
                      key={tag.id}
                      className="bg-gray-100 rounded-full px-4 py-2 flex items-center"
                    >
                      <span className="text-sm text-gray-800 mr-2">
                        {tag.name}
                      </span>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => handleEdit(tag)}
                          className="text-gray-400 hover:text-gray-500"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(tag.id)}
                          className="text-red-400 hover:text-red-500"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Delete Confirmation Modal */}
                      {showDeleteConfirm === tag.id && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                          <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">
                              Delete Tag
                            </h3>
                            <p className="text-sm text-gray-500 mb-4">
                              Are you sure you want to delete "{tag.name}"? This
                              will remove the tag from all prompts.
                            </p>
                            <div className="flex justify-end space-x-3">
                              <button
                                onClick={() => setShowDeleteConfirm(null)}
                                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => handleDelete(tag.id)}
                                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
